import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useCallback, useEffect, useState } from "react";
import ScrollToTop from "./ScrollToTop";
import Header from "./components/shared/Header";
import Footer from "./components/shared/Footer";
import TheMission from "./components/footer/TheMission";
import TheTeam from "./components/footer/TheTeam";
import EmployerHowItWorks from "./components/footer/EmployerHowItWorks";
import HowToPickGreatTargets from "./components/footer/HowToPickGreatTargets";
import Pricing from "./components/footer/Pricing";
import CandidateHowItWorks from "./components/footer/CandidateHowItWorks";
import FAQ from "./components/footer/FAQ";
import HowMatchingWorks from "./components/footer/HowMatchingWorks";
import BlogIndex from "./components/footer/BlogIndex";
import ContactUs from "./components/footer/ContactUs";
import PrivacyPolicy from "./components/footer/PrivacyPolicy";
import CookiePolicy from "./components/footer/CookiePolicy";
import CookieConsent from "./components/shared/CookieConsent";
import TermsOfService from "./components/footer/TermsOfService";
import HomePage from "./components/shared/HomePage";
import SignIn from "./components/shared/SignIn";
import CandidateSignup from "./components/candidate/CandidateSignUp";
import CompleteProfile from "./components/candidate/CompleteProfile";
import EmployerSignup from "./components/employer/EmployerSignUp";
import TargetSignUp from "./components/employer/TargetSignUp";
import CompanySignUp from "./components/employer/CompanySignUp";
import UserProfile from "./components/shared/UserProfile";
import Assessment from "./components/candidate/Assessment";
import Results from "./components/candidate/Results";
import EmployerDashboard from "./components/employer/EmployerDashboard";
import SuperUserDashboard from "./components/superUser/SuperUserDashboard";
import EmailPasswordReset from "./components/shared/EmailPasswordReset";
import AuthSuccess from "./components/shared/AuthSuccess";
import AcceptInvite from "./components/shared/AcceptInvite";
import VerifyEmail from "./components/shared/VerifyEmail";
import EmailPreferences from "./components/shared/EmailPreferences";
import Matches from "./components/candidate/Matches";

const App = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // On initial load, decode token from localStorage if present
    useEffect(() => {
        const token = localStorage.getItem("token");
        if (token) {
            try {
                const decoded = jwtDecode(token);
                // Keep a copy of the decoded user in localStorage
                localStorage.setItem("user", JSON.stringify(decoded));
                setUser(decoded);
            } catch (err) {
                console.error("❌ Invalid token:", err);
                localStorage.removeItem("token");
                localStorage.removeItem("user");
                setUser(null);
            }
        }

        setLoading(false); // ✅ Ensure loading state is updated
    }, []);

    useEffect(() => {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
            setUser(JSON.parse(storedUser));
        }
    }, []);

    // Provide a callback for SignIn to set user state immediately
    const handleSignInSuccess = useCallback((decodedUser) => {
        setUser(decodedUser);
    }, []);

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <Router>
            <div className="flex flex-col min-h-screen">
                <ScrollToTop />
            <Header />
            {/* Add top padding to ensure content doesn't overlap the fixed header */}
            <div className="pt-16 flex flex-col flex-grow">
            <Routes>
                <Route path="/" element={<HomePage />} />
                <Route
                    path="/auth-success"
                    element={<AuthSuccess onSignInSuccess={handleSignInSuccess} />}
                />
                <Route path="/the-mission" element={<TheMission />} />
                <Route path="/the-team" element={<TheTeam />} />
                <Route path="/employer-how-it-works" element={<EmployerHowItWorks />} />
                <Route path="/how-to-pick-great-targets" element={<HowToPickGreatTargets />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/candidate-how-it-works" element={<CandidateHowItWorks />} />
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/how-matching-works" element={<HowMatchingWorks />} />
                <Route path="/guides" element={<BlogIndex />} />

                <Route
                    path="/signin"
                    element={<SignIn onSignInSuccess={handleSignInSuccess} />}
                />

                <Route path="/candidate-signup" element={<CandidateSignup />} />
                <Route path="/complete-profile" element={<CompleteProfile />} />
                <Route path="/employer-signup" element={<EmployerSignup />} />

                <Route
                    path="/profile"
                    element={
                        user ? (
                            <UserProfile
                                userId={user.userId}
                                isSuperUser={user.isSuperUser}
                            />
                        ) : (
                            <Navigate to="/signin" />
                        )
                    }
                />
                <Route
                    path="/assessment"
                    element={user ? <Assessment /> : <Navigate to="/signin" />}
                />
                <Route
                    path="/results"
                    element={user ? <Results /> : <Navigate to="/signin" />}
                />
                <Route
                    path="/employer-dashboard"
                    element={
                        user?.role !== "Candidate" && user?.role !== "Target" ? (
                            <EmployerDashboard />
                        ) : (
                            <Navigate to="/signin" />
                        )
                    }
                />
                <Route
                    path="/admin"
                    element={
                        user?.isSuperUser ? <SuperUserDashboard /> : <Navigate to="/signin" />
                    }
                />
                <Route path="/email-password-reset" element={<EmailPasswordReset />} />
                <Route path="/target-signup" element={<TargetSignUp />} />
                <Route path="/company-signup" element={<CompanySignUp />} />
                <Route path="/accept-invite" element={<AcceptInvite />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/email-preferences" element={<EmailPreferences />} />

                {/* New Matches Route */}
                <Route
                    path="/matches"
                    element={user ? <Matches /> : <Navigate to="/signin" />}
                />

                <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            </div>
            <CookieConsent />
            <Footer />
            </div>
        </Router>
    );
};

export default App;
